import React from 'react';
import { Settings, RotateCcw } from 'lucide-react';
import { ImportSettings as ImportSettingsType } from '../../../../core/types/import.types';

interface Props {
  settings: ImportSettingsType;
  onUpdateSettings: (settings: Partial<ImportSettingsType>) => void;
  onResetSettings: () => void;
}

export const ImportSettings: React.FC<Props> = ({
  settings,
  onUpdateSettings,
  onResetSettings,
}) => {
  return (
    <div className="p-4 space-y-5 border-l border-zinc-700 bg-zinc-800/30 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-200">
          <Settings size={16} />
          Import Settings
        </div>
        <button
          onClick={onResetSettings}
          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          title="Reset to defaults"
        >
          <RotateCcw size={14} />
        </button>
      </div>

      {/* Image Options */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Image Options</h4>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.compressImages}
            onChange={(e) => onUpdateSettings({ compressImages: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Compress images
        </label>

        {settings.compressImages && (
          <div className="ml-6 space-y-2">
            <label className="block text-xs text-zinc-400">
              Quality: {settings.compressionQuality}%
            </label>
            <input
              type="range"
              min="10"
              max="100"
              value={settings.compressionQuality}
              onChange={(e) => onUpdateSettings({ compressionQuality: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-xs text-zinc-500">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.generateThumbnails}
            onChange={(e) => onUpdateSettings({ generateThumbnails: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Generate thumbnails
        </label>

        {settings.generateThumbnails && (
          <div className="ml-6">
            <label className="block text-xs text-zinc-400 mb-1">
              Thumbnail size
            </label>
            <select
              value={settings.thumbnailSize}
              onChange={(e) => onUpdateSettings({ thumbnailSize: parseInt(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm bg-zinc-900 border border-zinc-600 rounded text-zinc-300 focus:outline-none focus:border-blue-500 cursor-pointer"
            >
              <option value="64">64px</option>
              <option value="128">128px</option>
              <option value="256">256px</option>
            </select>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.autoDetectSpriteSheets}
            onChange={(e) => onUpdateSettings({ autoDetectSpriteSheets: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Auto-detect sprite sheets
        </label>

        {settings.autoDetectSpriteSheets && (
          <div className="ml-6 space-y-2">
            <label className="block text-xs text-zinc-400 mb-1">
              Minimum dimensions
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.spriteSheetThreshold.width}
                onChange={(e) => onUpdateSettings({
                  spriteSheetThreshold: {
                    ...settings.spriteSheetThreshold,
                    width: parseInt(e.target.value) || 256,
                  },
                })}
                className="w-20 px-2 py-1 text-sm bg-zinc-900 border border-zinc-600 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
                min="64"
                max="4096"
              />
              <span className="text-zinc-500">x</span>
              <input
                type="number"
                value={settings.spriteSheetThreshold.height}
                onChange={(e) => onUpdateSettings({
                  spriteSheetThreshold: {
                    ...settings.spriteSheetThreshold,
                    height: parseInt(e.target.value) || 256,
                  },
                })}
                className="w-20 px-2 py-1 text-sm bg-zinc-900 border border-zinc-600 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
                min="64"
                max="4096"
              />
            </div>
          </div>
        )}
      </div>

      {/* Naming Options */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Naming Options</h4>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.keepOriginalNames}
            onChange={(e) => onUpdateSettings({ keepOriginalNames: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Keep original file names
        </label>
        <p className="ml-6 text-xs text-zinc-500">
          {settings.keepOriginalNames
            ? 'Files will keep their original names'
            : 'Files will be renamed with unique IDs'}
        </p>
      </div>

      {/* File Options */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">File Options</h4>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.createSubfolders}
            onChange={(e) => onUpdateSettings({ createSubfolders: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Create subfolders by type
        </label>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.overwriteExisting}
            onChange={(e) => onUpdateSettings({ overwriteExisting: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Overwrite existing files
        </label>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-zinc-100 transition-colors">
          <input
            type="checkbox"
            checked={settings.preserveMetadata}
            onChange={(e) => onUpdateSettings({ preserveMetadata: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
          />
          Preserve file metadata
        </label>
      </div>
    </div>
  );
};
